import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { DeployAllProvider } from './contexts/DeployAllContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import GlobalLoader from './components/GlobalLoader';
import ToastContainer from './components/ToastContainer';
import { DeployAllPanel } from './components/DeployAllPanel';
import { DeployAllWidget } from './components/DeployAllWidget';
import { getAdminProductConfig } from './config/productConfig';

// Auth
import LoginPage from './pages/LoginPage';

// Dashboard & Overview
import SuperAdminCommandCenter from './pages/SuperAdminCommandCenter';

// Tenant Management
import Tenants from './pages/Tenants';
import FinanceHub from './pages/FinanceHub';
import TenantProfile from './pages/TenantProfile';
import TenantBilling from './pages/TenantBilling';
import TenantAffiliatePanel from './pages/TenantAffiliatePanel';
import SuperAdminLocationDetail from './pages/SuperAdminLocationDetail';
import SuperAdminPosTerminalDetail from './pages/SuperAdminPosTerminalDetail';
import PaymentsListPage from './pages/PaymentsListPage';
import PaymentDetailPage from './pages/PaymentDetailPage';
import AuditLogPage from './pages/AuditLogPage';

// Packages & Products
import PackagesPage from './pages/PackagesPage';
import ProductsPage from './pages/ProductsPage';
import DealsPage from './pages/DealsPage';
import DealEditPage from './pages/DealEditPage';
import InventoryHubPage from './pages/inventory/InventoryHubPage';
import DeviceEditorPage from './pages/inventory/DeviceEditorPage';
import BundlesEditorPage from './pages/inventory/BundlesEditorPage';
import SalesPage from './pages/inventory/SalesPage';
import OrdersPage from './pages/inventory/OrdersPage';
import UnitPage from './pages/inventory/UnitPage';
import ReportsPage from './pages/inventory/ReportsPage';
import FinancingWizardPage from './pages/inventory/FinancingWizardPage';
import AgreementsPage from './pages/inventory/AgreementsPage';
import FinanceBookPage from './pages/inventory/FinanceBookPage';
import BusinessTransfersPage from './pages/business-transfers/BusinessTransfersPage';
import TransferDetailPage from './pages/business-transfers/TransferDetailPage';
import MenusPage from './pages/MenusPage';

// Internal Tools
import ToolLibraryPage from './pages/tools/ToolLibraryPage';
import ToolActivityPage from './pages/tools/ToolActivityPage';
import DataMigrationListPage from './pages/tools/DataMigrationListPage';
import DataMigrationJobPage from './pages/tools/DataMigrationJobPage';

// AI & Analytics
// SuperAdminAIStatus.tsx and AiUsageDashboard.tsx are retired as routes but
// kept on disk for side-by-side comparison until the merge is signed off.
import AiControlCenter from './pages/ai/AiControlCenter';
import LegacyAiRedirect from './pages/ai/LegacyAiRedirect';
import SuperAdminAnalytics from './pages/SuperAdminAnalytics';

// Affiliates
import SuperAdminAffiliates from './pages/SuperAdminAffiliates';
import EstateOverviewPage from './pages/estate/EstateOverviewPage';
import EstateBusinessesPage from './pages/estate/EstateBusinessesPage';
import EstateBusinessDetailPage from './pages/estate/EstateBusinessDetailPage';
import EstateFeePlansPage from './pages/estate/EstateFeePlansPage';
import EstateFeesReportPage from './pages/estate/EstateFeesReportPage';
import EstateStripeSettingsPage from './pages/estate/EstateStripeSettingsPage';
import IdentityVerificationSettings from './pages/settings/IdentityVerificationSettings';
import SocialAppKeysPage from './pages/integrations/SocialAppKeysPage';
import { SOCIAL_PUBLISHING_ENABLED } from './config/socialPublishing';
import CommissionPlansPage from './pages/CommissionPlansPage';
import CommissionPlanEditPage from './pages/CommissionPlanEditPage';
import CertificationPortal from './pages/CertificationPortal';
import AffiliateDetailPage from './pages/AffiliateDetailPage';
import AffiliateDashboard from './pages/AffiliateDashboard';
import { merchantRoute } from './utils/merchantUrl';

// Notifications

// Support
import SuperAdminSupportTickets from './pages/SuperAdminSupportTickets';
import SuperAdminAppIssuesQueue from './pages/SuperAdminAppIssuesQueue';
import ControlsPage from './pages/controls/ControlsPage';
import { isDevHost } from './config/devOnly';

// DEV-ONLY visual harness for Website Analytics (fake API, no login). Behind
// `import.meta.env.DEV`, which Vite replaces with `false` in a production
// build — the route and this lazy chunk are both dropped from dist.
const WebAnalyticsHarness = import.meta.env.DEV ? lazy(() => import('./dev/WebAnalyticsHarness')) : null;

// Activity
import ActivityLogPage from './pages/ActivityLogPage';
import DeploymentDashboard from './pages/DeploymentDashboard';
import RepoDetail from './pages/RepoDetail';

// POS
import PosConfigTab from './pages/pos/PosConfigTab';
import PosDefaultsPage from './pages/pos/PosDefaultsPage';
import PaymentProcessorsPage from './pages/pos/PaymentProcessorsPage';
import FleetDevicePage from './pages/pos/device/FleetDevicePage';
import FleetMonitoringPage from './pages/fleet/FleetMonitoringPage';
import { LegacyDeviceRoute } from './pages/fleet/legacyRedirects';
import { DeviceLinkBase, FLEET_MONITORING_DEVICE_BASE } from './pages/pos/device/deviceLinkBase';
import ReleasesPage from './pages/releases/ReleasesPage';
import MobileAppsPage from './pages/mobile-apps/MobileAppsPage';

// Legal — document governance. Policies and Agreements are the SAME engine
// (one document store, one editor) split by `kind`; Signatures waits on envelopes.
import PoliciesList from './pages/legal/PoliciesList';
import AgreementsList from './pages/legal/AgreementsList';
import SendAgreementPage from './pages/legal/SendAgreementPage';
import DocumentDetail from './pages/legal/DocumentDetail';
import SignaturesPage from './pages/legal/SignaturesPage';
import PacksPage from './pages/legal/PacksPage';
import BindingsPage from './pages/legal/BindingsPage';
import CategoriesPage from './pages/legal/CategoriesPage';
import DocumentsHubPage from './pages/legal/DocumentsHubPage';
import CompanyProfilePage from './pages/legal/CompanyProfilePage';

// Staff — RubyTill's OWN employees (staff-service): hiring, onboarding
// workflows, panel authorization, and each member's own portal.
import { StaffAccessProvider } from './hooks/useStaffAccess';
import StaffManagementPage from './pages/staff/StaffManagementPage';
import StaffDetailPage from './pages/staff/StaffDetailPage';
import StaffRolesPage from './pages/staff/StaffRolesPage';
import StaffPortalPage from './pages/staff/StaffPortalPage';
import MySecurityPage from './pages/security/MySecurityPage';


/** Leaves this app for an absolute URL (another app), replacing the history entry. */
function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}

function App() {
  const [sessionExpiredPopup, setSessionExpiredPopup] = useState(false);

  useEffect(() => {
    const product = getAdminProductConfig();
    document.title = `${product.adminTitle} - Super Admin Portal`;
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => {
      console.log('[App] Session expired — showing popup');
      setSessionExpiredPopup(true);
    };
    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, []);

  return (
    <AuthProvider>
      <SidebarProvider>
      <DeployAllProvider>
        <GlobalLoader />
        <ToastContainer />
        {/* Global Deploy All surfaces — modal + minimized widget live at app root
            so they survive route changes and (via localStorage) page refreshes. */}
        <DeployAllPanel />
        <DeployAllWidget />
        {sessionExpiredPopup && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', maxWidth: 380, width: '90%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111', margin: '0 0 8px' }}>Session Expired</h2>
              <p style={{ fontSize: 14, color: '#666', margin: '0 0 24px', lineHeight: 1.5 }}>
                Your session has expired. Please sign in again to continue.
              </p>
              <button
                onClick={() => {
                  setSessionExpiredPopup(false);
                  localStorage.removeItem('jwtToken');
                  localStorage.removeItem('refreshToken');
                  localStorage.removeItem('tokenExpiry');
                  window.location.href = '/login';
                }}
                style={{ width: '100%', padding: '12px 24px', fontSize: 15, fontWeight: 700, color: '#fff', background: '#111', border: 'none', borderRadius: 10, cursor: 'pointer' }}
              >
                Sign In
              </button>
            </div>
          </div>
        )}
        <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        {WebAnalyticsHarness && <Route path="/__dev/web-analytics" element={<Suspense fallback={null}><WebAnalyticsHarness /></Suspense>} />}
        {/* Partner sign-in / apply live in platform-app (the white split
            screens). These admin-host copies were an older, off-theme
            duplicate; old links and bookmarks are sent to the real ones. */}
        <Route path="/affiliate/login" element={<ExternalRedirect to={merchantRoute('/affiliate/login')} />} />
        <Route path="/affiliate/register" element={<ExternalRedirect to={merchantRoute('/affiliate/register')} />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <StaffAccessProvider>
                <Layout />
              </StaffAccessProvider>
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/command-center" replace />} />

          {/* Dashboard & Overview */}
          <Route path="command-center" element={<SuperAdminCommandCenter />} />

          {/* Tenant Management */}
          <Route path="finance" element={<FinanceHub />} />
          <Route path="tenants-list" element={<Tenants />} />
          <Route path="tenant/:tenantId" element={<TenantProfile />} />
          <Route path="tenant/:tenantId/billing" element={<TenantBilling />} />
          <Route path="tenant/:tenantId/location/:requestId" element={<SuperAdminLocationDetail />} />
          <Route path="tenant/:tenantId/pos-terminal/:terminalId" element={<SuperAdminPosTerminalDetail />} />
          <Route path="tenant/:tenantId/affiliate" element={<TenantAffiliatePanel />} />

          {/* Packages & Products */}
          <Route path="packages" element={<PackagesPage />} />
          <Route path="products" element={<ProductsPage />} />
          {/* /deals moved into Inventory > Sales as its "Deals" sub-tab — kept as
              a redirect so nothing that already links here breaks. */}
          <Route path="deals" element={<Navigate to="/inventory/deals" replace />} />
          <Route path="deals/new" element={<DealEditPage />} />
          <Route path="deals/:dealId" element={<DealEditPage />} />

          {/* Inventory & Financing Suite (wave W1). The merged tab-hub is
              retired: every former hub tab is its own side-nav route below
              (Layout.tsx's Inventory group); legacy hub paths redirect. */}
          <Route path="inventory" element={<Navigate to="/inventory/stock" replace />} />
          <Route path="inventory/stock" element={<InventoryHubPage tab="stock" />} />
          {/* The money book, moved off /inventory/stock (spec 2026-08-30).
              'finance-book', NOT 'agreements' — /inventory/agreements already
              means signature envelopes. */}
          <Route path="inventory/finance-book" element={<FinanceBookPage />} />
          {/* Creating and editing equipment is a PAGE now, not a modal over the
              list (2026-08-30) — the same DeviceEditor, rendered with
              variant="page". `?duplicate=<modelId>` opens it as a copy. */}
          <Route path="inventory/stock/new" element={<DeviceEditorPage />} />
          <Route path="inventory/stock/:modelId/edit" element={<DeviceEditorPage />} />
          <Route path="inventory/receiving" element={<InventoryHubPage tab="grn" />} />
          <Route path="inventory/vendors" element={<InventoryHubPage tab="vendors" />} />
          <Route path="inventory/adjustments" element={<InventoryHubPage tab="adjustments" />} />
          <Route path="inventory/damage" element={<InventoryHubPage tab="damage" />} />
          <Route path="inventory/provisioning" element={<InventoryHubPage tab="provisioning" />} />
          <Route path="inventory/brands" element={<InventoryHubPage tab="brands" />} />
          {/* Legacy merged-hub mounts — InventoryHubPage redirects these to the
              section route, honouring ?tab= (see its legacyTarget). */}
          <Route path="inventory/catalog" element={<InventoryHubPage defaultTab="stock" />} />
          <Route path="inventory/procurement" element={<InventoryHubPage defaultTab="grn" />} />
          {/* Sales — Bundles and Deals are route-per-section (sidebar "Sales" group);
              bare /inventory/sales redirects, honouring its legacy ?tab= param. */}
          <Route path="inventory/bundles" element={<SalesPage tab="bundles" />} />
          <Route path="inventory/deals" element={<SalesPage tab="deals" />} />
          <Route path="inventory/sales" element={<SalesPage />} />
          <Route path="inventory/orders" element={<OrdersPage />} />
          <Route path="business-transfers" element={<BusinessTransfersPage />} />
          <Route path="business-transfers/:transferId" element={<TransferDetailPage />} />
          {/* Device model catalog merged into Stock — old links land there. */}
          <Route path="inventory/models" element={<Navigate to="/inventory/stock" replace />} />
          <Route path="inventory/bundles-editor" element={<BundlesEditorPage />} />
          {/* Building a bundle is a route, not a query flag — the builder owns
              the screen, with no list behind it. `/new` is static so it always
              wins over `:bundleId`. */}
          <Route path="inventory/bundles-editor/new" element={<BundlesEditorPage />} />
          <Route path="inventory/bundles-editor/:bundleId" element={<BundlesEditorPage />} />
          <Route path="inventory/units/:unitId" element={<UnitPage />} />
          {/* MERGED 2026-08-30: the Financing LIST is now part of
              /inventory/stock (Equipment & Financing) — its stat tiles, its
              per-device policies and its agreement book all render there. The
              route stays as a redirect so bookmarks and in-app links keep
              working. The WIZARD below is untouched and still its own screen. */}
          <Route path="inventory/financing" element={<Navigate to="/inventory/stock" replace />} />
          <Route path="inventory/financing/new" element={<FinancingWizardPage />} />
          {/* Editing a policy is the SAME wizard, seeded from the device's
              live offers (2026-08-27) — `?edit=bundle:<id>` / `model:<id>`. */}
          <Route path="inventory/financing/edit" element={<FinancingWizardPage />} />
          <Route path="inventory/agreements" element={<AgreementsPage />} />
          <Route path="inventory/reports" element={<ReportsPage />} />
          <Route path="menus" element={<MenusPage />} />

          {/* Internal Tools — Tool Library + its audit trail */}
          <Route path="tools" element={<ToolLibraryPage />} />
          <Route path="tools/activity" element={<ToolActivityPage />} />
          <Route path="tools/data-migration" element={<DataMigrationListPage />} />
          <Route path="tools/data-migration/:tenantId/:jobId" element={<DataMigrationJobPage />} />

          {/* AI & Analytics */}
          {/* AI Control Center — one route, seven tabs (spec §6).
              The two retired paths redirect, preserving ?tab= and ?tenantId=. */}
          <Route path="ai" element={<AiControlCenter />} />
          <Route path="ai-status" element={<LegacyAiRedirect />} />
          <Route path="ai-usage" element={<LegacyAiRedirect />} />
          <Route path="analytics" element={<SuperAdminAnalytics />} />

          {/* Affiliates */}
          <Route path="affiliates" element={<SuperAdminAffiliates />} />
          <Route path="affiliates/certification" element={<CertificationPortal />} />
          <Route path="affiliates/:affiliateId" element={<AffiliateDetailPage />} />
          <Route path="affiliate-dashboard" element={<AffiliateDashboard />} />
          {/* Commission plans — the ONE place partner rates are edited.
              Sits under Partners; a deal points at a plan rather than
              carrying its own commission fields. */}
          <Route path="commission-plans" element={<CommissionPlansPage />} />
          {/* The editor is addressed by id so a plan can be linked to and
              reloaded. `new` is a reserved id: `uniquePlanId` slugifies a NAME,
              and no plan is ever minted with a bare `new` id. */}
          <Route path="commission-plans/new" element={<CommissionPlanEditPage />} />
          <Route path="commission-plans/:planId" element={<CommissionPlanEditPage />} />

          {/* Notifications — folded into the merged Support Center (2026-08-26).
              SuperAdminNotifications.tsx (the old page) has been deleted. */}
          <Route path="notifications" element={<Navigate to="/support" replace />} />

          {/* Support */}
          <Route path="support" element={<SuperAdminSupportTickets />} />

          {/* In-app issue reports (FAB-submitted from platform-app) */}
          {/* One route for list + detail: the id segment opens the detail as a
              drawer OVER the list (component reads it from the URL), so the
              list never unmounts and browser back just closes the drawer. */}
          <Route path="app-issues/*" element={<SuperAdminAppIssuesQueue />} />
          {/* System → Controls: console approvals + developer access. Dev hosts
              only, like the issue console it controls — on admin.rubytill.com
              the route is never registered (config/devOnly.ts). */}
          {isDevHost() && <Route path="controls" element={<ControlsPage />} />}

          {/* POS */}
          <Route path="pos-config" element={<PosConfigTab />} />
          {/* Platform processor credentials (MagTek UniGate). A child of /pos-config so its nav permission covers it. */}
          <Route path="pos-config/payment-processors" element={<PaymentProcessorsPage />} />
          {/* Fleet Monitoring (spec 2026-10-04) — section /fleet-monitoring covers the device pages too (longest prefix). */}
          <Route path="fleet-monitoring" element={<FleetMonitoringPage />} />
          <Route path="fleet-monitoring/devices/:deviceId" element={<DeviceLinkBase.Provider value={FLEET_MONITORING_DEVICE_BASE}><FleetDevicePage /></DeviceLinkBase.Provider>} />
          {/* One device, everything about it. A CHILD path of pos-config so the
              nav permission that covers /pos-config covers this too — a role
              that can see the fleet can see a device in it, and splitting that
              would let someone open the table and be refused every row in it. */}
          <Route path="pos-config/fleet/:deviceId" element={<LegacyDeviceRoute />} />
          {/* The release hub (Apps / Builds / Rollout). ONE route — ReleasesPage
              is the only file registering it; do not add a second. */}
          <Route path="releases" element={<ReleasesPage />} />
          <Route path="mobile-apps" element={<MobileAppsPage />} />

          {/* Legal — policy authoring, versioning and publishing */}
          {/* Ruby Estate (Task 34) */}
          <Route path="estate-admin" element={<EstateOverviewPage />} />
          <Route path="estate-admin/businesses" element={<EstateBusinessesPage />} />
          <Route path="estate-admin/businesses/:tenantId" element={<EstateBusinessDetailPage />} />
          <Route path="estate-admin/fee-plans" element={<EstateFeePlansPage />} />
          <Route path="estate-admin/fees" element={<EstateFeesReportPage />} />
          <Route path="estate-admin/stripe" element={<EstateStripeSettingsPage />} />
          {SOCIAL_PUBLISHING_ENABLED && <Route path="integrations/social" element={<SocialAppKeysPage />} />}

          {/* Task 19 — Settings > ID Verification */}
          <Route path="identity-admin" element={<IdentityVerificationSettings />} />

          <Route path="legal" element={<Navigate to="/legal/policies" replace />} />
          <Route path="legal/policies" element={<PoliciesList />} />
          <Route path="legal/policies/:documentId" element={<DocumentDetail />} />
          <Route path="legal/agreements" element={<AgreementsList />} />
          {/* Send a vendor / employee / partner / software agreement. Declared
              BEFORE the :documentId route, or "send" is read as a document id. */}
          <Route path="legal/agreements/send" element={<SendAgreementPage />} />
          {/* Same editor as a policy — an agreement is a document with kind:'agreement'. */}
          <Route path="legal/agreements/:documentId" element={<DocumentDetail />} />
          <Route path="legal/signatures" element={<SignaturesPage />} />
          {/* Categories — "where is this offered?", as rows rather than an enum. */}
          <Route path="legal/categories" element={<CategoriesPage />} />
          {/* Company Identity Master — the one row every generated document's
              company_* merge fields project from. */}
          <Route path="legal/company" element={<CompanyProfilePage />} />
          {/* Attachable-documents engine (spec 2026-08-26): packs + the hub. */}
          <Route path="legal/packs" element={<PacksPage />} />
          {/* Binding registry (spec 2026-08-29 §6, §8): which documents a device,
              financing term, package, checkout, business type or jurisdiction owes. */}
          <Route path="legal/bindings" element={<BindingsPage />} />
          <Route path="legal/hub" element={<DocumentsHubPage />} />

          {/* Card transactions (NMI + external card detail) */}
          <Route path="payments" element={<PaymentsListPage />} />
          <Route path="payments/:id" element={<PaymentDetailPage />} />
          <Route path="tenant/:tenantId/audit-log/payment-config" element={<AuditLogPage />} />

          {/* Activity */}
          <Route path="activity" element={<ActivityLogPage />} />
          <Route path="deployments" element={<DeploymentDashboard />} />
          <Route path="pos-defaults" element={<PosDefaultsPage />} />
          <Route path="deployments/:owner/:repo" element={<RepoDetail />} />

          {/* Staff — RubyTill internal (hire/fire, onboarding workflows,
              roles & panel access, and every member's own portal) */}
          <Route path="staff" element={<StaffManagementPage />} />
          <Route path="staff/roles" element={<StaffRolesPage />} />
          <Route path="staff/:staffId" element={<StaffDetailPage />} />
          <Route path="staff-portal" element={<StaffPortalPage />} />
          <Route path="my-security" element={<MySecurityPage />} />
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/command-center" replace />} />
      </Routes>
      </DeployAllProvider>
      </SidebarProvider>
    </AuthProvider>
  );
}

export default App;
